import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCreditGuard } from '../contexts/CreditGuardContext';
import portalTranslations from '../translations/portal';
import EmmaSidebar from './EmmaSidebar';
import {
  Compass, BarChart3, History, FileText, Settings, Users, Key, CreditCard,
  LogOut, Globe, User, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  LayoutDashboard, Building2, AlertTriangle, X, Coins, Monitor, Bot,
  PenLine, Brain, Sparkles, Home, Zap,
} from 'lucide-react';
import NotificationBell from './NotificationBell';

const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const { credits, showModal, modalData, dismissModal } = useCreditGuard();
  const t = portalTranslations[lang];

  const [collapsed, setCollapsed] = useState(false);
  const [lowCreditDismissed, setLowCreditDismissed] = useState(false);
  const [emmaOpen, setEmmaOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(() => {
    const path = location.pathname;
    if ((String(path || '')).includes('/portal/emma')) return 'emma';
    if ((String(path || '')).includes('/portal/ai-tools') || (String(path || '')).includes('/portal/roadmap') || (String(path || '')).includes('/portal/agent') || (String(path || '')).includes('/portal/process-optimizer') || (String(path || '')).includes('/portal/virtual-training')) return 'tools';
    if ((String(path || '')).includes('/portal/analytics') || (String(path || '')).includes('/portal/history') || (String(path || '')).includes('/portal/reports') || (String(path || '')).includes('/portal/accounting') || (String(path || '')).includes('/portal/company-profile')) return 'dashboard';
    if ((String(path || '')).includes('/portal/profile') || (String(path || '')).includes('/portal/api-keys') || (String(path || '')).includes('/portal/billing') || (String(path || '')).includes('/portal/desktop-probe')) return 'settings';
    return 'tools';
  });

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleGroup = (group) => {
    setOpenGroup(openGroup === group ? '' : group);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navGroups = [
    {
      id: 'emma',
      label: 'Emma AI',
      icon: Sparkles,
      items: [
        { path: '/portal/emma/writing', label: lang === 'nl' ? 'Schrijfassistent' : 'Writing Assistant', icon: PenLine },
        { path: '/portal/emma/meeting-brain', label: 'Meeting Brain', icon: Brain },
        { path: '/portal/agent-workflows', label: lang === 'nl' ? 'AI Agents' : 'AI Agents', icon: Bot },
        { path: '/portal/emma-proactive', label: lang === 'nl' ? 'Proactieve Emma' : 'Proactive Emma', icon: Zap },
      ],
    },
    {
      id: 'tools',
      label: lang === 'nl' ? 'AI Tools' : 'AI Tools',
      icon: Compass,
      items: [
        { path: '/portal/ai-tools', label: lang === 'nl' ? 'Alle AI Tools' : 'All AI Tools', icon: Compass },
      ],
    },
    {
      id: 'dashboard',
      label: lang === 'nl' ? 'Dashboard' : 'Dashboard',
      icon: LayoutDashboard,
      items: [
        { path: '/portal/analytics', label: t.nav.analytics, icon: BarChart3 },
        { path: '/portal/history', label: t.nav.history, icon: History },
        { path: '/portal/reports', label: t.nav.reports, icon: FileText },
        { path: '/portal/accounting', label: lang === 'nl' ? 'Boekhouding' : 'Accounting', icon: Building2 },
        { path: '/portal/company-profile', label: lang === 'nl' ? 'Bedrijfsprofiel' : 'Company Profile', icon: User },
      ],
    },
    {
      id: 'settings',
      label: t.nav.settings,
      icon: Settings,
      items: [
        { path: '/portal/profile', label: lang === 'nl' ? 'Profiel' : 'Profile', icon: User },
        { path: '/portal/api-keys', label: lang === 'nl' ? 'API Sleutels' : 'API Keys', icon: Key },
        { path: '/portal/desktop-probe', label: lang === 'nl' ? 'Desktop Probe' : 'Desktop Probe', icon: Monitor },
        { path: '/portal/billing', label: t.nav.billing, icon: CreditCard },
      ],
    },
  ];

  return (
    <div className="portal-layout" data-testid="portal-layout">
      {/* Top Bar */}
      <header className="portal-topbar" data-testid="portal-topbar">
        <div className="topbar-left">
          <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle" aria-label="Toggle sidebar">
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
          <div className="topbar-logo-wrap" onClick={() => navigate('/portal')} data-testid="topbar-logo">
            <img src="/logo.svg" alt="Krypton9" className="topbar-logo-img" style={{ borderRadius: 8, objectFit: "contain" }} />
            <span className="topbar-logo-text">KRYPTON9</span>
          </div>
        </div>
        <div className="topbar-right">
          <button onClick={toggleLang} className="lang-toggle-btn" data-testid="lang-toggle" title={lang === 'en' ? 'Switch to Dutch' : 'Schakel naar Engels'}>
            <Globe size={15} />
            <span>{lang === 'en' ? 'NL' : 'EN'}</span>
          </button>
          <button type="button" onClick={() => navigate('/portal/billing')} className="lang-toggle-btn" data-testid="topbar-credits"
            title={lang === 'nl' ? 'Credits en facturatie' : 'Credits and billing'}
            aria-label={lang === 'nl' ? 'Credits en facturatie' : 'Credits and billing'}>
            <Coins size={18} aria-hidden="true" />
          </button>
          <NotificationBell />
          <button type="button" className="topbar-user" data-testid="topbar-user" onClick={() => navigate('/portal/profile')} title={lang === 'nl' ? 'Profielinstellingen openen' : 'Open profile settings'} aria-label={lang === 'nl' ? 'Profielinstellingen openen' : 'Open profile settings'}>
            <User size={16} />
            <span className="topbar-username">{user?.full_name || 'User'}</span>
          </button>
          <button onClick={handleLogout} className="topbar-logout" data-testid="logout-btn" title={t.nav.logout}>
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`portal-sidebar ${collapsed ? 'collapsed' : ''}`} data-testid="portal-sidebar">
        <nav className="sidebar-nav">
          <button
            className={`sidebar-item back-to-portal ${location.pathname === '/portal' ? 'active' : ''}`}
            onClick={() => navigate('/portal')}
            data-testid="nav-back-to-portal"
            title={lang === 'nl' ? 'Terug naar Portaal' : 'Back to Portal'}
          >
            <Home size={16} />
            {!collapsed && <span>{lang === 'nl' ? 'Terug naar Portaal' : 'Back to Portal'}</span>}
          </button>

          {(Array.isArray(navGroups) ? navGroups : []).map((group) => {
            const GroupIcon = group.icon;
            const isGroupActive = (Array.isArray(group.items) ? group.items : []).some((item) => isActive(item.path));
            const isOpen = openGroup === group.id;

            return (
              <div key={group.id} className={`sidebar-group ${isGroupActive ? 'active-group' : ''}`}>
                <button
                  className={`sidebar-group-header ${isOpen ? 'open' : ''}`}
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`nav-group-${group.id}`}
                >
                  <div className="sidebar-group-label">
                    <GroupIcon size={18} />
                    {!collapsed && <span>{group.label}</span>}
                  </div>
                  {!collapsed && (isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </button>

                {isOpen && !collapsed && (
                  <div className="sidebar-group-items">
                    {(Array.isArray(group.items) ? group.items : []).map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                          onClick={() => navigate(item.path)}
                          data-testid={`nav-${item.path.split('/').pop()}`}
                        >
                          <Icon size={16} />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {collapsed && (
                  <div className="sidebar-group-items-collapsed">
                    {(Array.isArray(group.items) ? group.items : []).map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                          onClick={() => navigate(item.path)}
                          title={item.label}
                          data-testid={`nav-${item.path.split('/').pop()}-icon`}
                        >
                          <Icon size={16} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className={`portal-content ${collapsed ? 'sidebar-collapsed' : ''} ${emmaOpen ? 'emma-open' : ''}`}>
        {/* Low credit warning banner */}
        {credits !== null && credits <= 10 && credits > 0 && !lowCreditDismissed && (
          <div className="credit-warning-banner" data-testid="low-credit-banner">
            <div className="credit-warning-content">
              <AlertTriangle size={16} />
              <span>
                {lang === 'nl'
                  ? `Je hebt nog ${credits} credits over. Koop meer credits om AI tools te blijven gebruiken.`
                  : `You have ${credits} credits remaining. Purchase more credits to keep using AI tools.`}
              </span>
              <button
                className="credit-warning-cta"
                onClick={() => navigate('/portal/billing')}
                data-testid="low-credit-buy-btn"
              >
                <Coins size={14} />
                {lang === 'nl' ? 'Credits kopen' : 'Buy credits'}
              </button>
            </div>
            <button className="credit-warning-close" onClick={() => setLowCreditDismissed(true)} data-testid="low-credit-dismiss">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Zero credit warning banner (not dismissable) */}
        {credits !== null && credits <= 0 && (
          <div className="credit-warning-banner credit-warning-zero" data-testid="zero-credit-banner">
            <div className="credit-warning-content">
              <AlertTriangle size={16} />
              <span>
                {lang === 'nl'
                  ? 'Je hebt geen credits meer. Koop credits om AI tools te kunnen gebruiken.'
                  : 'You have no credits left. Purchase credits to use AI tools.'}
              </span>
              <button
                className="credit-warning-cta"
                onClick={() => navigate('/portal/billing')}
                data-testid="zero-credit-buy-btn"
              >
                <Coins size={14} />
                {lang === 'nl' ? 'Credits kopen' : 'Buy credits'}
              </button>
            </div>
          </div>
        )}

        {children}
      </main>

      {/* Insufficient Credits Modal */}
      {showModal && modalData && (
        <div className="credit-modal-overlay" data-testid="credit-modal-overlay" onClick={dismissModal}>
          <div className="credit-modal" data-testid="credit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="credit-modal-icon">
              <CreditCard size={32} />
            </div>
            <h3>{lang === 'nl' ? 'Onvoldoende Credits' : 'Insufficient Credits'}</h3>
            <p className="credit-modal-msg">{modalData.message}</p>
            <div className="credit-modal-stats">
              <div className="credit-stat">
                <span className="credit-stat-label">{lang === 'nl' ? 'Nodig' : 'Required'}</span>
                <span className="credit-stat-value credit-stat-cost">{modalData.cost}</span>
              </div>
              <div className="credit-stat">
                <span className="credit-stat-label">{lang === 'nl' ? 'Beschikbaar' : 'Available'}</span>
                <span className="credit-stat-value credit-stat-balance">{modalData.balance}</span>
              </div>
            </div>
            <div className="credit-modal-actions">
              <button
                className="credit-modal-buy"
                onClick={() => { dismissModal(); navigate('/portal/billing'); }}
                data-testid="credit-modal-buy-btn"
              >
                <Coins size={16} />
                {lang === 'nl' ? 'Credits Kopen' : 'Buy Credits'}
              </button>
              <button className="credit-modal-close" onClick={dismissModal} data-testid="credit-modal-close-btn">
                {lang === 'nl' ? 'Sluiten' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emma AI Assistant - Always present */}
      <EmmaSidebar onOpenChange={setEmmaOpen} />
    </div>
  );
};

export default PortalLayout;
